import * as React from 'react';
import * as analytics from '../../analytics';
import { RouteComponentProps, withRouter } from 'react-router';
import { connect } from 'react-redux';
import { ApplicationState } from '../../store';
import * as Metadata from "../../entities/Metadata";
import { UserState, isInReadonlyMode } from '../../store/User';
import { canCreate, canUpdate } from '../../store/permissions';
import { Icon, IconButton, IContextualMenuItem, MessageBar, MessageBarType } from 'office-ui-fabric-react';
import * as ProjectsListStore from '../../store/ProjectsListStore';
import CategoryColorStatusView from '../views/list/columns/CategoryColorStatusView';
import { nameof } from '../../store/services/metadataService';
import { ILayoutActions, LayoutService } from '../utils/LayoutService';
import { IInsightsData, EntityType, StatusCategory, IWarning } from '../../entities/common';
import { TenantState } from '../../store/Tenant';
import { notUndefined } from '../utils/common';
import { LayoutsState } from '../../store/layouts';
import PersistEntityViewMenu from '../common/PersistEntityViewMenu';
import * as StatusDescriptorFactory from '../../entities/StatusDescriptorFactory';
import { buildAiProjectDigestMenuItemIfEnabled, buildProjectPpmInsightsMenuItemIfEnabled, getArchiveDialogContent } from '../project/ProjectsList';
import ProjectSettingsPanel from '../project/ProjectSettingsPanel';
import AIDigestPanel from '../project/AIDigestPanel';
import EntityHeader from '../common/EntityHeader';
import ProjectLogo from './ProjectLogo';
import Link from '../common/Link';
import FavoriteToggle from '../common/FavoriteToggle';
import { SummaryEntityWarning } from '../common/warnings/SummaryEntityWarning';
import RefreshProjectButton from '../common/sectionsControl/uiControls/projectSummaryControl/RefreshProjectButton';
import TasksIcon from '../common/sectionsControl/uiControls/projectSummaryControl/TasksIcon';
import LinkedSystemIcons from '../common/sectionsControl/uiControls/summaryControl/LinkedSystemIcons';
import RemoveDialog from '../common/RemoveDialog';
import TasksButton from '../common/sectionsControl/uiControls/projectSummaryControl/TasksButton';
import ApplyLayoutConfirmationDialog from '../common/ApplyLayoutConfirmationDialog';
import ProjectSetBaselineConfirmation from './ProjectSetBaselineConfirmation';
import PrivateProjectIcon from '../common/PrivateProjectIcon';
import EntityHeaderMoreMenu from '../common/EntityHeaderMoreMenu';
import CategoryColorStageView from '../views/list/columns/CategoryColorStageView';
import { getStageFieldLabel } from '../views/list/columns/ColorStageView';
import { AttachmentsIcon } from '../attachment/AttachmentsIcon';
import { IAttachmentActions } from '../../entities/Subentities';
import { SectionsSettingsProps, withSectionsSettings } from '../common/sectionsControl/withSectionsSettings';
import { AttachmentsComponent, useAttachmentsComponent } from '../attachment/AttachmentsComponent';

interface IActions {
    updateImage?: (logo: File) => void;
    removeImage?: () => void;
    setFavorite?: (isFavorite: boolean) => void;
    openConfigurationPanel: () => void;
    layoutActions?: ILayoutActions;
    cloneProject: () => void;
    removeProject: () => void;
    archiveProject?: () => void;
    updateSettings?: (data: ProjectsListStore.ProjectSettingsUpdateData) => void;
    setProjectBaseline: (setProjectBaseline: boolean, setTasksBaseline: boolean, setKeyDatesBaseline: boolean) => void;
    attachmentsActions?: IAttachmentActions;
}

type UserPreferences = {
    showOnlyPinnedAttachments: boolean;
}

type OwnProps = {
    entity: ProjectsListStore.ProjectInfo;
    warnings?: IWarning[];
    warningsTypeMap?: string[];
    navigateToDetails?: boolean;
    hideWarnings?: boolean;
    disableChangeView?: boolean;
    actions: IActions;
}

type StateProps = {
    user: UserState;
    tenant: TenantState;
    projectFields: Metadata.Field[];
    layouts: LayoutsState;
}

type Props = OwnProps & StateProps & RouteComponentProps<{}> & SectionsSettingsProps<UserPreferences>;

const trackProps = { entityType: EntityType.Project };

const ProjectHeader = (props: Props) => {
    const [isRemoveDialogOpen, setIsRemoveDialogOpen] = React.useState(false);
    const [isArchiveDialogOpen, setIsArchiveDialogOpen] = React.useState(false);
    const [layoutToApply, setLayoutToApply] = React.useState<Metadata.Layout>();
    const [isProjectSettingsOpen, setIsProjectSettingsOpen] = React.useState(false);
    const [isAIDigestPanelOpen, setIsAIDigestPanelOpen] = React.useState(false);
    const [isProjectBaselinePanelOpen, setIsProjectBaselinePanelOpen] = React.useState(false);
    const { menu, componentProps } = useAttachmentsComponent(props.entity.attachments, props.user, props.actions.attachmentsActions);
    const { entity, tenant, warnings, layouts, user, projectFields, actions, sectionsSettings, onSaveSettings } = props;
    const canClone = canCreate(user.permissions.project) && (entity.isEditable || entity.isArchived) && !entity.isPrivate;
    const _updateProjectSettings = (data: ProjectsListStore.ProjectSettingsUpdateData) => {
        actions.updateSettings?.(data);

        const keys = Object.keys(data);
        if (keys.includes(nameof<IInsightsData>("statusCalculationDisabled"))) {
            analytics.trackToggle("Calculated automatically - 'Project statuses' updated", props.user, !data.statusCalculationDisabled, trackProps);
        }
        if (keys.includes("keyDateStatusCalculationDisabled")) {
            analytics.trackToggle("Calculated automatically - 'Key dates statuses' updated", props.user, !data.keyDateStatusCalculationDisabled, trackProps);
        }
        if (keys.includes("taskStatusCalculationDisabled")) {
            analytics.trackToggle("Calculated automatically - 'Tasks statuses' updated", props.user, !data.taskStatusCalculationDisabled, trackProps);
        }
        if (keys.includes("warningsCalculationDisabled")) {
            analytics.trackToggle("Calculated automatically - 'Warnings' updated", props.user, !data.warningsCalculationDisabled, trackProps);
        }
        if (keys.includes("progressCalculationType")) {
            analytics.trackEvent("Progress calculation changed", props.user, { progressCalculationType: data.progressCalculationType, ...trackProps });
        }
    };
    
    const trackMoreMenu = (message: string, properties?: any) => analytics.trackEvent(`Project Details Menu: ${message}`, props.user, properties);

    const _isMoreMenuDisabled = () => {
        if (!props.entity.isEditable) {
            return false;
        }
        return !(canCreate(props.user.permissions.project) || canUpdate(props.user.permissions.project));
    };
    const moreMenuItems: IContextualMenuItem[] = [
        entity.isEditable ? menu.attachmentsMenuItem : undefined,
        buildProjectPpmInsightsMenuItemIfEnabled(tenant, user, entity, props.history, () => trackMoreMenu("PPM Insights")),
        buildAiProjectDigestMenuItemIfEnabled(tenant, user, entity.isArchived, entity.isEditable, () => {
            setIsAIDigestPanelOpen(true);
            trackMoreMenu("AI Project Digest");
        }),
        {
            key: 'project-settings',
            iconProps: { iconName: 'Settings' },
            name: 'Project Settings',
            onClick: () => {
                setIsProjectSettingsOpen(true);
                trackMoreMenu("Project Settings");
            },
        },
        entity.isEditable ? {
            key: 'project-baseline',
            iconProps: { iconName: 'PPMXBaseline' },
            name: 'Set Project Baseline',
            onClick: () => {
                setIsProjectBaselinePanelOpen(true);
                trackMoreMenu("Set Project Baseline");
            }
        } : undefined,
        entity.canConfigure && actions.layoutActions?.applyLayout
            ? {
                ...LayoutService.buildApplyLayoutMenuItem(layouts, (layout) => {
                    setLayoutToApply(layout);
                    trackMoreMenu("Apply Layout", { layoutName: layout.name });
                }, entity.layoutId),
                disabled: !entity.isEditable
            } : undefined,
        {
            key: 'cloneProject',
            iconProps: { iconName: 'Copy' },
            name: 'Clone Project',
            title: entity.isArchived
                ? "A project will be duplicated, using a limited dataset from an existing archived project."
                : entity.isPrivate
                    ? "Private projects cannot be cloned."
                    : undefined,
            disabled: !canClone,
            onClick: () => {
                actions.cloneProject();
                trackMoreMenu("Clone Project");
            }
        },
        !!actions.archiveProject && !entity.isArchived ? {
            key: 'archiveProject',
            iconProps: { iconName: 'Archive' },
            name: 'Archive Project',
            title: entity.isPrivate ? "Private projects cannot be archived." : undefined,
            disabled: !entity.isEditable || entity.isPrivate,
            onClick: () => {
                setIsArchiveDialogOpen(true);
                trackMoreMenu("Archive Project");
            }
        } : undefined,
        {
            key: 'deleteProject',
            iconProps: { iconName: 'Delete' },
            name: 'Delete Project',
            className: 'more-deleteButton',
            disabled: !entity.isEditable && !entity.isArchived,
            onClick: () => {
                setIsRemoveDialogOpen(true);
                trackMoreMenu("Delete Project");
            }
        }
    ].filter(notUndefined);
    const readonlyMode = isInReadonlyMode(user, tenant, entity);
    const isConnectPanelAvailable = entity.isEditable || entity.sourceInfos.length && readonlyMode;
    const overallStatusField = projectFields.find(_ => _.name === nameof<ProjectsListStore.ProjectAttrs>("OverallStatus"))!;
    const statusDescriptor = StatusDescriptorFactory.createStatusDescriptor(overallStatusField);
    const statusOption = statusDescriptor.getOptionOrDefault(entity.attributes.OverallStatus, StatusCategory.NA);
    const stageOption = entity.attributes.Stage;
    return <>
        <EntityHeader
            entity={entity}
            nameTitle={entity.attributes.Name}
            logo={<ProjectLogo imageId={entity.imageId}
                onChanged={entity.isEditable ? actions.updateImage : undefined}
                onRemove={entity.isEditable ? actions.removeImage : undefined} />}
            name={<>
                {entity.isPrivate && <PrivateProjectIcon />}
                {props.navigateToDetails
                    ? <Link href={ProjectsListStore.getProjectPath(entity)} title="Navigate to Project Dashboard">
                        {entity.attributes.Name}
                    </Link>
                    : entity.attributes.Name}
            </>}
            leftSide={<>
                {!entity.isArchived && actions.setFavorite && <FavoriteToggle
                    isFavorite={entity.isFavorite}
                    disabled={readonlyMode}
                    onChange={actions.setFavorite}
                />}
                {entity.isArchived
                    ? <div className="color-status" title="Archived">
                        <div className={`align-center status Archived`}>
                            <Icon iconName="Archive" className="status-icon" />
                            <span className="status-label">Archived</span>
                        </div>
                    </div>
                    : <CategoryColorStatusView
                        statusOption={statusOption}
                        trend={entity.insights.statuses[nameof<ProjectsListStore.ProjectAttrs>("OverallStatus")]?.trend}
                        title={overallStatusField?.label}
                    />}
                {stageOption && <CategoryColorStageView fieldLabel={getStageFieldLabel(EntityType.Project)} stageOption={stageOption} withBorder />}
                {!props?.hideWarnings && props.warningsTypeMap && <SummaryEntityWarning warnings={warnings ?? []}
                    message="There might be some data inconsistency in your project. For more details please review items marked with warning icons below."
                    commonWarningTypes={props.warningsTypeMap} />}

            </>}
            buttons={<>
                {!props.navigateToDetails && <TasksButton entity={entity} />}
                {!entity.isArchived && <RefreshProjectButton entity={entity} />}
                <TasksIcon entity={entity} />
                <LinkedSystemIcons
                    sourceInfos={entity.sourceInfos}
                    taskProgresses={entity.calculation.taskProgresses}
                    showTitle
                    showMetrics
                    allowNavigate
                    entityType={EntityType.Project} />
                {
                    !!entity.attachments?.length && <AttachmentsIcon
                        readonly={!entity.isEditable}
                        attachmentsMenuItem={menu.attachmentsMenuItem}
                        attachments={entity.attachments}
                        actions={actions.attachmentsActions}
                        showOnlyPinned={!!sectionsSettings?.showOnlyPinnedAttachments}
                        onShowOnlyPinnedChange={_ => onSaveSettings?.({
                            valueBySettingNameMap: { [nameof<UserPreferences>('showOnlyPinnedAttachments')]: _ },
                            parentSettingsPathKeys: []
                        })}
                    />
                }
                {!entity.isArchived && <IconButton iconProps={{ iconName: 'PPMXLink' }}
                    className="connect-launcher"
                    title="Connect"
                    disabled={!isConnectPanelAvailable}
                    onClick={() => actions.openConfigurationPanel()} />}
                <EntityHeaderMoreMenu
                    entity={entity}
                    entityType={entity.isArchived ? EntityType.ArchivedProject : EntityType.Project}
                    items={moreMenuItems}
                    disabled={_isMoreMenuDisabled()}
                    onClickMenuItem={(name: string) => trackMoreMenu(name)} />
            </>}
            views={actions.layoutActions
                ? <PersistEntityViewMenu
                    layoutsState={layouts}
                    entity={entity}
                    entityType={EntityType.Project}
                    readonlyMode={readonlyMode}
                    disableChangeView={props.disableChangeView}
                    user={user}
                    viewLayout={actions.layoutActions?.viewLayout}
                    saveLayout={entity.isEditable ? actions.layoutActions?.saveLayout : undefined}
                    savePinnedViews={entity.canConfigure
                        ? actions.layoutActions?.updateEntityPinnedViews
                        : undefined}
                    saveEntityLayout={entity.canConfigure || readonlyMode
                        ? actions.layoutActions?.updateEntityLayout
                        : undefined}
                    deleteLayout={actions.layoutActions?.deleteLayout}
                    disableManageConfiguration={!!entity.isArchived}
                    profileLayoutId={user.permissions.projectProfileLayoutId}
                    layoutIdByEntityId={user.permissions.layoutIdByProjectIdMap?.[entity.id]}
                />
                : undefined}
        ></EntityHeader>
        {
            isRemoveDialogOpen && <RemoveDialog
                onClose={() => setIsRemoveDialogOpen(false)}
                onComplete={() => { actions.removeProject(); }}
                dialogContentProps={{
                    title: "Delete project",
                    subText: `Are you sure you want to delete project "${entity!.attributes.Name}"?`
                }}
                confirmButtonProps={{ text: "Delete" }} />
        }
        {
            isArchiveDialogOpen && actions.archiveProject && <RemoveDialog
                onClose={() => setIsArchiveDialogOpen(false)}
                onComplete={() => { actions.archiveProject?.(); }}
                dialogContentProps={getArchiveDialogContent([entity])}
                confirmButtonProps={{ text: "Archive" }} >
                <MessageBar messageBarType={MessageBarType.warning}>Restoring an archived project will not be feasible, but it will be possible to clone it.</MessageBar>
            </RemoveDialog>
        }
        {
            isProjectSettingsOpen && <ProjectSettingsPanel
                entity={entity}
                readonlyMode={readonlyMode}
                onDismiss={() => setIsProjectSettingsOpen(false)}
                onSave={_updateProjectSettings} />
        }
        {
            isAIDigestPanelOpen && <AIDigestPanel
                project={entity}
                onDismiss={() => setIsAIDigestPanelOpen(false)} />
        }
        {
            layoutToApply && <ApplyLayoutConfirmationDialog
                onConfirm={() => actions.layoutActions!.applyLayout!(layoutToApply)}
                onDismiss={() => setLayoutToApply(undefined)}
                entityType={EntityType.Project}
                layoutName={layoutToApply!.name}
            />
        }
        {
            isProjectBaselinePanelOpen && <ProjectSetBaselineConfirmation
                onConfirm={(setProjectBaseline, setTasksBaseline, setKeyDatesBaseline) => { props.actions.setProjectBaseline?.(setProjectBaseline, setTasksBaseline, setKeyDatesBaseline) }}
                onDismiss={() => setIsProjectBaselinePanelOpen(false)} />
        }
        <AttachmentsComponent
            {...componentProps}
            showToastMessage
            attachments={props.entity.attachments ?? []}
        />
    </>;
}

function mapStateToProps(state: ApplicationState, ownProps?: OwnProps): StateProps {
    const projectFields = state.fields[EntityType.Project];
    return {
        user: state.user,
        tenant: state.tenant,
        projectFields: projectFields.allIds.map(_ => projectFields.byId[_]),
        layouts: state.layouts[EntityType.Project]
    }
}

export default withSectionsSettings<OwnProps>(withRouter(connect(mapStateToProps)(ProjectHeader)));